import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { countries } from 'countries-list';

import { getPriceCurrency, getPriceValue } from '@/models/core/values';

import { SCREEN_SIZE } from '@/constants/common';
import { PRODUCT_SIZE } from '@/constants/treatment';
import { SWIZZ_CURRENCY } from '@/constants/currency';
import { COUNTRY_ISO } from '@/constants/countries';
import { getIngredientTotalConcentration } from '@/models/core/Ingredient';

const getNumberStringWithFixedDecimals = (fractionDigits, number) => {
  return parseFloat(Math.round(number * 100) / 100).toFixed(fractionDigits);
};

const getScrollBarWidth = () => {
  const elementWithScrollOverflow = document.createElement('div');

  elementWithScrollOverflow.style.overflowY = 'scroll';
  elementWithScrollOverflow.style.width = '50px';
  elementWithScrollOverflow.style.height = '50px';

  document.body.append(elementWithScrollOverflow);

  const scrollWidth = elementWithScrollOverflow.offsetWidth - elementWithScrollOverflow.clientWidth;

  elementWithScrollOverflow.remove();

  return scrollWidth;
};

const adjustHeaderPaddingBeforeToggleScrollBar = disableScroll => {
  const headerElement = document.querySelector('.app-header');

  if (!headerElement) {
    return;
  }

  if (disableScroll) {
    const scrollbarWidthValue = `${getScrollBarWidth()}px`;

    setTimeout(() => {
      /* In order to prevent header content flickering
       * we need to postpone this updates until next cycle
       * (as `disableBodyScroll` internally use setTimeout).
       */
      headerElement.style.paddingRight = scrollbarWidthValue;
      document.body.style.marginRight = scrollbarWidthValue;
    });
    return;
  }

  setTimeout(() => {
    headerElement.style.paddingRight = null;
    document.body.style.marginRight = null;
  });
};

const toggleDocumentScroll = (targetElement, disableScroll) => {
  if (disableScroll) {
    adjustHeaderPaddingBeforeToggleScrollBar(disableScroll);
    disableBodyScroll(targetElement, {
      reserveScrollBarGap: true
    });
    return;
  }

  clearAllBodyScrollLocks();
  adjustHeaderPaddingBeforeToggleScrollBar(disableScroll);
};

/**
 * Format price.
 *
 * @param {Price} price
 * @param {string} localeWithRegion
 * @param {number} [fractionalDigits]
 * @return {string}
 */
const formatPrice = ({ price, localeWithRegion, fractionalDigits }) => {
  const currency = getPriceCurrency(price);
  const priceValue = getPriceValue(price);

  const isSwizzCurrency = currency === SWIZZ_CURRENCY;

  const defaultFractionalDigits = isSwizzCurrency ? 1 : 2;
  const fractionalDigitsToUse =
    typeof fractionalDigits === 'number' ? fractionalDigits : defaultFractionalDigits;

  return new Intl.NumberFormat(localeWithRegion, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: fractionalDigitsToUse,
    minimumFractionDigits: fractionalDigitsToUse
  }).format(priceValue);
};

const formatProductSize = ({ value, units }) => `${value}${units}`;

const getCountryDisplayName = (countryCode, localeWithRegion) => {
  const regionNamesInEnglish = new Intl.DisplayNames([localeWithRegion], { type: 'region' });

  return regionNamesInEnglish.of(countryCode);
};

const normalizeProductSize = (productName, size) => {
  const { units } = PRODUCT_SIZE[productName];
  return { value: +size, units };
};

const getIngredientConcentration = (/* { ingredientName, nbOfCapsules } */) => {
  throw Error('Should be removed');
};

const formatIngredientConcentration = ({ ingredient, nameTranslation }) => {
  const concentration = getNumberStringWithFixedDecimals(
    1,
    getIngredientTotalConcentration(ingredient)
  );

  return nameTranslation ? `${nameTranslation} ${concentration}%` : `${concentration}%`;
};

const getPhotoTypePictureById = async photoTypeId => {
  const { default: imagePath } = await import(
    `@/assets/images/profile-pictures/${photoTypeId}.jpg`
  );
  return imagePath;
};

const deleteSpaces = string => string.replace(/\s/g, '');

const transformVerificationCode = codeNumber =>
  deleteSpaces(codeNumber)
    .split('')
    .map((digit, index) => (index === 2 ? `${digit} ` : digit))
    .join('')
    .trim();

const getScreenSize = () => {
  const matchList = [
    { size: SCREEN_SIZE.EXTRA_LARGE, matches: window.matchMedia('(min-width: 1920px)').matches },
    { size: SCREEN_SIZE.LARGE, matches: window.matchMedia('(min-width: 1264px)').matches },
    { size: SCREEN_SIZE.MEDIUM, matches: window.matchMedia('(min-width: 960px)').matches },
    { size: SCREEN_SIZE.SMALL, matches: window.matchMedia('(min-width: 600px)').matches },
    { size: SCREEN_SIZE.EXTRA_SMALL, matches: true }
  ];

  const { size } = matchList.find(({ matches }) => matches);

  return size;
};

const sortInAlphabeticalOrder = (previousString, currentString) =>
  previousString > currentString ? 1 : -1;

const sleep = async time =>
  new Promise(done => {
    setTimeout(done, time);
  });

const getCountryCurrency = country => {
  if (!country) {
    return '';
  }

  // handling multiple currencies in Switzerland
  if (country.toUpperCase() === COUNTRY_ISO.SWITZERLAND) {
    return 'CHF';
  }

  const { currency = '' } = countries[country.toUpperCase()] || {};

  return currency;
};

const deepFreeze = object => {
  const propNames = Object.getOwnPropertyNames(object);

  propNames.forEach(name => {
    const value = object[name];

    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  });

  return Object.freeze(object);
};

const isChromeBrowser =
  navigator.userAgent.indexOf('Chrome') !== -1 &&
  navigator.vendor.indexOf('Google Inc') !== -1 &&
  navigator.userAgent.indexOf('Edg') === -1;

export {
  getNumberStringWithFixedDecimals,
  toggleDocumentScroll,
  formatPrice,
  formatProductSize,
  normalizeProductSize,
  getIngredientConcentration,
  formatIngredientConcentration,
  getPhotoTypePictureById,
  deleteSpaces,
  transformVerificationCode,
  getScreenSize,
  sortInAlphabeticalOrder,
  sleep,
  getCountryCurrency,
  deepFreeze,
  getCountryDisplayName,
  isChromeBrowser
};
